import { ParamMap, Params } from '@angular/router';
import { IBase } from 'src/app/models';
import { MessageAdminAlertsFilter, MessageOrigin, MessageReceiver, NotificationEvent } from 'src/app/models/enums';
import { parseEnumList, parseIdList, serializeIdList, serializeStringList } from './filterUtils';

export interface IMessagesFilters {
  selectedClients: IBase[];
  selectedSenders: IBase[];
  selectedReceivers: IBase[];
  selectedOrigin: MessageOrigin | null;
  selectedEvents: NotificationEvent[];
  selectedAdminAlerts: MessageAdminAlertsFilter | null;
}

export interface IMessagesPanelFilters extends IMessagesFilters {
  receiverType?: MessageReceiver;
  selectedExpeditionService?: IBase;
  selectedExpeditionRoute?: IBase;
}

export const MESSAGE_FILTER_KEYS = ['clients', 'senders', 'receivers', 'origin', 'events', 'adminAlerts'] as const;

export function emptyMessagesFilters(): IMessagesFilters {
  return {
    selectedClients: [],
    selectedSenders: [],
    selectedReceivers: [],
    selectedOrigin: null,
    selectedEvents: [],
    selectedAdminAlerts: null,
  };
}

export function serializeMessagesFilters(f: IMessagesFilters): Params {
  return {
    clients: serializeIdList(f.selectedClients),
    senders: serializeIdList(f.selectedSenders),
    receivers: serializeIdList(f.selectedReceivers),
    origin: f.selectedOrigin,
    events: serializeStringList(f.selectedEvents),
    adminAlerts: toAdminAlertsFlag(f.selectedAdminAlerts)?.toString() ?? null,
  };
}

export function deserializeMessagesFilters(params: ParamMap): IMessagesFilters {
  const selectedOrigin = parseMessageOrigin(params.get('origin'));
  return {
    selectedClients: parseIdList(params.get('clients')),
    selectedSenders: parseIdList(params.get('senders')),
    selectedReceivers: parseIdList(params.get('receivers')),
    selectedOrigin,
    selectedEvents: parseEnumList<NotificationEvent>(params.get('events')),
    selectedAdminAlerts: adminAlertsForOrigin(selectedOrigin, parseAdminAlertsFlag(params.get('adminAlerts'))),
  };
}

export function parseMessagesFiltersFromStorage(json: string | null | undefined): IMessagesFilters | null {
  if (!json) return null;
  try {
    const f = JSON.parse(json);
    const selectedOrigin = parseMessageOrigin(f.selectedOrigin);
    return {
      selectedClients: Array.isArray(f.selectedClients) ? f.selectedClients : [],
      selectedSenders: Array.isArray(f.selectedSenders) ? f.selectedSenders : [],
      // The messages filter modal stores receivers under a nested key for legacy reasons.
      selectedReceivers: Array.isArray(f.receivers?.selectedReceivers)
        ? f.receivers.selectedReceivers
        : (Array.isArray(f.selectedReceivers) ? f.selectedReceivers : []),
      selectedOrigin,
      selectedEvents: Array.isArray(f.selectedEvents) ? f.selectedEvents : [],
      selectedAdminAlerts: adminAlertsForOrigin(selectedOrigin, parseStoredAdminAlerts(f.selectedAdminAlerts)),
    };
  } catch {
    return null;
  }
}

export function parseMessagesPanelFiltersFromStorage(json: string | null | undefined): IMessagesPanelFilters | null {
  const filters = parseMessagesFiltersFromStorage(json);
  if (!filters) return null;
  try {
    const receivers = JSON.parse(json!).receivers;
    return {
      ...filters,
      receiverType: receivers?.selectedReceiver ?? undefined,
      selectedExpeditionService: receivers?.selectedExpeditionService ?? undefined,
      selectedExpeditionRoute: receivers?.selectedExpeditionRoute ?? undefined,
    };
  } catch {
    return filters;
  }
}

export function serializeMessagesPanelFiltersForStorage(f: IMessagesPanelFilters): string {
  return JSON.stringify({
    selectedClients: f.selectedClients,
    selectedSenders: f.selectedSenders,
    selectedOrigin: f.selectedOrigin,
    selectedEvents: f.selectedEvents,
    selectedAdminAlerts: f.selectedAdminAlerts,
    receivers: {
      selectedReceiver: f.receiverType,
      selectedExpeditionService: f.selectedExpeditionService,
      selectedExpeditionRoute: f.selectedExpeditionRoute,
      selectedReceivers: f.selectedReceivers,
    },
  });
}

export function toAutomaticFlag(origin: MessageOrigin | null): boolean | null {
  if (origin === null) return null;
  return origin === MessageOrigin.AUTOMATIC;
}

export function toMessageOrigin(checkedOrigins: string[]): MessageOrigin | null {
  return checkedOrigins.length === 1 ? parseMessageOrigin(checkedOrigins[0]) : null;
}

export function toCheckedOrigins(origin: MessageOrigin | null): MessageOrigin[] {
  return origin ? [origin] : Object.values(MessageOrigin);
}

export function toAdminAlertsFlag(adminAlerts: MessageAdminAlertsFilter | null): boolean | null {
  if (adminAlerts === MessageAdminAlertsFilter.ONLY) return true;
  if (adminAlerts === MessageAdminAlertsFilter.EXCLUDE) return false;
  return null;
}

export function toAdminAlertsFilter(checkedOptions: string[]): MessageAdminAlertsFilter | null {
  return parseStoredAdminAlerts(checkedOptions[0]);
}

export function toCheckedAdminAlerts(adminAlerts: MessageAdminAlertsFilter | null): MessageAdminAlertsFilter[] {
  return [adminAlerts ?? MessageAdminAlertsFilter.ALL];
}

function parseAdminAlertsFlag(raw: string | null): MessageAdminAlertsFilter | null {
  if (raw === 'true') return MessageAdminAlertsFilter.ONLY;
  if (raw === 'false') return MessageAdminAlertsFilter.EXCLUDE;
  return null;
}

function parseStoredAdminAlerts(stored: unknown): MessageAdminAlertsFilter | null {
  if (stored === true || stored === MessageAdminAlertsFilter.ONLY) return MessageAdminAlertsFilter.ONLY;
  if (stored === MessageAdminAlertsFilter.EXCLUDE) return MessageAdminAlertsFilter.EXCLUDE;
  return null;
}

function adminAlertsForOrigin(
  origin: MessageOrigin | null,
  adminAlerts: MessageAdminAlertsFilter | null,
): MessageAdminAlertsFilter | null {
  return origin === MessageOrigin.MANUAL ? null : adminAlerts;
}

function parseMessageOrigin(raw: string | null | undefined): MessageOrigin | null {
  return Object.values(MessageOrigin).includes(raw as MessageOrigin) ? (raw as MessageOrigin) : null;
}
